import {Set} from 'immutable';
import {CENTERED_THRESHOLD} from '~/constants/CanvasConstants';
import {INK_COLORS} from '~/constants/InkConstants';
import {EFFECTS} from '~/constants/TextEffectConstants';
import {COLOR_MODES} from '~/constants/UploadConstants';
import {isBlack, isNone, isWhite} from '~/data/accessors/color';
import {isSameColor} from '~/utils/colors';
import {isInternal} from '~/utils/user';

export function isArt(iconType) {
  return iconType === 'CLIPART';
}

export function isText(iconType) {
  return iconType === 'TEXT';
}

export function isUpload(iconType) {
  return iconType === 'UPLOAD';
}

export function getNewIcon(newIconArray, oldIconArray) {
  return newIconArray.find(icon => oldIconArray.indexOf(icon) === -1);
}

export function isNames(iconType) {
  return iconType === 'NAMES';
}

export function isNumbers(iconType) {
  return iconType === 'NUMBERS';
}

export function isNamesOrNumbers(iconType) {
  return isNames(iconType) || isNumbers(iconType);
}

export function isWithinConstraint(dimension, icon, constraint) {
  const coord = dimension === 'width' ? 'x' : 'y';
  const rightBottomBound =
    icon[coord] + icon[dimension] / 2 + constraint[dimension] / 2;
  const leftTopBound = Math.round(
    icon[coord] - icon[dimension] / 2 + constraint[dimension] / 2
  );
  return leftTopBound >= 0 && rightBottomBound <= constraint[dimension];
}

export function iconColors(icon) {
  if (isText(icon.type)) {
    return textIconColors(icon);
  } else if (isUpload(icon.type)) {
    return uploadIconColors(icon);
  } else if (isNamesOrNumbers(icon.type)) {
    return namesAndNumbersIconColors(icon);
  } else {
    return artIconColors(icon);
  }
}

export function isCentered(icon) {
  return Math.abs(icon.x) < CENTERED_THRESHOLD;
}

export function findIcon(icons, id) {
  const matchFunction = icon => icon.id === id;
  const index = icons.findIndex(matchFunction);
  const icon = icons.find(matchFunction);
  return {
    index,
    icon,
    backwardable: index > 0,
    forwardable: index !== -1 && index < icons.length - 1
  };
}

export function isRotationAllowed(icon) {
  if (isText(icon.type)) {
    const autoFit = hasAutoFitStrategy(icon);
    return !autoFit;
  }
  return true;
}

/*
 * TEXT ICON HELPERS
 */

export function getAllowedEffects(icon) {
  if (!isText(icon.type)) {
    return [];
  }
  if (!hasAutoFitStrategy(icon)) {
    return EFFECTS;
  }
  return EFFECTS.filter(effect => effect.name !== 'curve');
}

export function hasAutoFitStrategy(icon) {
  return isText(icon.type) && icon.fit === 'occupy';
}

export function isAutoFitAllowed(icon) {
  const isRotated = icon.rotation !== 0;
  const hasCurveEffect = icon.effectType === 'arc';
  return !isRotated && !hasCurveEffect && !isMultilineText(icon);
}

export function isFontSizeAllowed(icon) {
  return isText(icon.type) && !hasAutoFitStrategy(icon);
}

export function isMultilineAllowed(icon) {
  return isText(icon.type) && !hasAutoFitStrategy(icon);
}

/*
 * UPLOAD ICON HELPERS
 */

export function isFullColorUpload(icon) {
  return (
    isUpload(icon.type) &&
    icon.colorMode === COLOR_MODES.PHOTO &&
    !icon.singleColor
  );
}

export function areSpotColorsModified(icon) {
  if (isUpload(icon.type)) {
    return icon.spotColors.some(spotColor => {
      return spotColor.palette !== spotColor.user;
    });
  }
  return false;
}

export function isUploadIconModified(icon) {
  if (isUpload(icon.type)) {
    const props = ['horizontalFlip', 'singleColor', 'verticalFlip', 'rotation'];
    return props.some(prop => icon[prop]) || areSpotColorsModified(icon);
  }
  return false;
}

export function getBackgroundSpotColor(uploadIcon) {
  const {backgroundColor} = uploadIcon;
  return (
    backgroundColor &&
    uploadIcon.spotColors.find(spotColor => {
      return isSameColor(spotColor.original, backgroundColor);
    })
  );
}

export function isBackgroundVisible(uploadIcon) {
  const backgroundSpotColor = getBackgroundSpotColor(uploadIcon);
  return !!backgroundSpotColor && !isNone(backgroundSpotColor.user);
}

function uploadIconColors(icon) {
  if (icon.singleColor) {
    return Set([icon.color]);
  }
  switch (icon.colorMode) {
    case COLOR_MODES.SPOT:
    case COLOR_MODES.MONOCHROME:
      return icon.spotColors
        .map(color => color.user)
        .filterNot(color => isNone(color))
        .toSet();
    default:
      return Set();
  }
}

/*
 * ================================================================================================
 */

/*
 * CLIPART ICON HELPERS
 */

export function canBeClipartInverted(icon) {
  return !!(isArt(icon.type) && icon.clipartVersions.inverted);
}

export function isClipartInverted(icon) {
  return !!(
    canBeClipartInverted(icon) &&
    icon.clipartVersions.inverted.clipartId === icon.clipartId
  );
}

export function isClipartMultiColor(icon) {
  return isArt(icon.type) && icon.colors.size > 1 && !icon.blackwhite;
}

export function isClipartLicensed(icon) {
  const {clipartId, clipartVersions} = icon;

  if (!clipartVersions) return false;

  return Object.values(clipartVersions).some(
    version => version.clipartId === clipartId.toString() && version.licensed
  );
}

export function isClipartRecoloringEnabled(icon) {
  const {blackwhite, colors} = icon;

  if (!colors?.size) return false;

  const isOnlyBlack =
    colors.size === 1 && colors.every(color => isBlack(color?.original));

  return isClipartLicensed(icon) ? isOnlyBlack || blackwhite : true;
}

export function clipartHasSingleColorToggle(icon) {
  if (isClipartLicensed(icon)) {
    return false;
  }

  const {blackwhite, colors} = icon;
  const showToggleInTest = isInternal() && colors.size > 1 && !blackwhite;
  const showToggleInControl = !isInternal() && !blackwhite;

  // Non-blackwhite clipart need to get their 'singleColor' property set in the control
  return showToggleInTest || showToggleInControl;
}

export const isColorWhiteOrTransparent = color => {
  const original = color?.get ? color.get('original') : color?.original;
  const user = color?.get ? color.get('user') : color?.user;

  // If original is white, check if user is also white or none/transparent
  if (isWhite(original)) {
    // If user is white, none/transparent, or not specified (defaults to black), return true
    if (!user || isWhite(user) || isNone(user) || isBlack(user)) {
      return true;
    }
  }

  return false;
};

export function clipartHasWhiteToggler(icon = {}) {
  const {colors, singleColor} = icon;

  if (!colors?.size) return false;

  return (
    !singleColor && colors.size > 1 && colors.some(isColorWhiteOrTransparent)
  );
}

function artIconColors(icon) {
  if (icon.singleColor) {
    const colors = [icon.color];

    const iconOriginalColorsHasWhite = icon.colors.some(
      c => c.original && isWhite(c.original)
    );

    if (!icon.transparent && iconOriginalColorsHasWhite) {
      colors.push(INK_COLORS.White);
    }

    return Set(colors);
  }

  return icon.colors
    .map(color => (color.user ? color.user : color))
    .filter(color => !isNone(color))
    .toSet();
}

/*
 * ================================================================================================
 */

/*
 * CLIPART ICON HELPERS
 */

function textIconColors(icon) {
  var colors = Set();

  if (icon.outlineWidth > 0) {
    colors = colors.add(icon.outlineColor);
  }
  if (!isNone(icon.color)) {
    colors = colors.add(icon.color);
  }

  return colors;
}

export function textIconHandlesClearfill(icon) {
  return icon.outlineWidth > 0;
}

export function isMultilineText(icon) {
  return isText(icon.type) && /(?:\n|\r)\S/.test(icon.value);
}

/*
 * ================================================================================================
 */

/*
 * N&N ICON HELPERS
 */

function namesAndNumbersIconColors(icon) {
  return Set([icon.color]);
}

/*
 * ================================================================================================
 */
